import { RefObject, useEffect, useState } from 'react';
import { throttle } from 'underscore';

import { isDefined } from '../../utils/variables';

/**
 * The throttle rate should match as closely as possible with the render cycle for a
 * smooth experience.
 *
 * 16 ms is equivalent to 60 FPS.
 */
const throttleRate60FPS = 16;

/**
 * Updates the mouse position within the frame of the ref object.
 * client contains the pixel positions of the mouse within the element.
 * ndc is a normalized value between -1 and 1 used for three js typically.
 *
 * Throttle rate defaults to 60 FPS (16ms)
 */
export const useMousePosition = (
  ref?: RefObject<HTMLElement>,
  throttleRate: number = throttleRate60FPS,
) => {
  const [mousePosition, setMousePosition] = useState({
    client: { x: 0, y: 0 },
    ndc: { x: -1, y: -1 },
  });

  useEffect(() => {
    if (!isDefined(ref?.current)) {
      return;
    }

    const element = ref?.current;
    const handleMouseMove = (event: MouseEvent) => {
      const bounds = element.getBoundingClientRect();
      const x = event.clientX - bounds.left;
      const y = event.clientY - bounds.top;

      // Adjusting the calculation to return NDC.
      const ndcX = (x / bounds.width) * 2 - 1;
      const ndcY = -((y / bounds.height) * 2 - 1); // Invert y to match the typical 3D coordinate system.

      setMousePosition({
        client: { x, y },
        ndc: { x: ndcX, y: ndcY },
      });
    };

    const callback = throttle(handleMouseMove, throttleRate);
    element.addEventListener('mousemove', callback);
    return () => {
      element.removeEventListener('mousemove', callback);
    };
  }, [ref, throttleRate]);

  return mousePosition;
};
